'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["content", "gridLayouts"];
import http from 'hub-http/clients/apiClient';
import httpWithNonIdempotentRetries from 'ContentEditorUI/legacyHoldingPattern/apiClientWithNonIdempotentRetries';
import { getCategory } from 'ContentComponents/utils/contentModelUtils';
import I18n from 'I18n';
import { convertObjectToCamelCase, convertObjectToSnakeCase } from '../utils/helpers';
import rpcClient from 'ContentEditorUI/api/RpcClient';
import { convertJsonToFieldValueAvro } from 'rpc-client-utils';
import { autoSaveStagedPageRpc, manualSaveStagedPageRpc } from '../../__generated__/chirp/com/hubspot/contentstaging/rpc/ContentStagingPagesRpc';
const SAVE_TIMEOUT = 15000;
let editorUpdateId = 0;
let editorSessionId;

// Defer to refering to moment until later (because it isn't available yet to use)
const getEditorSessionId = () => {
  if (editorSessionId == null) {
    editorSessionId = I18n.moment.utc().valueOf();
  }
  return editorSessionId;
};
export const getBaseContentUrl = ({
  baseUrl,
  categoryId,
  subcategory,
  contentId
}) => {
  const fullCategory = getCategory(categoryId, subcategory);
  const postfix = contentId ? `/${contentId}` : '';
  return `${baseUrl}/${fullCategory.api_slug}${postfix}`;
};
export const fetch = baseContentUrl => {
  return http.get(`${baseContentUrl}/buffer`);
};
export const deleteBuffer = baseContentUrl => {
  return http.post(`${baseContentUrl}/discard-buffer`);
};
export const fetchHardSavedContent = ({
  baseContentUrl,
  query = {}
}) => {
  return http.get(`${baseContentUrl}`, {
    query
  });
};
export const getSaveQuery = (editingInfo, shouldAvoidSnakeCasing) => {
  const defaultQuery = {
    if_unmodified_since_ticks: editingInfo.last_edit_ticks,
    editor_session_id: getEditorSessionId(),
    editor_update_id: editorUpdateId++
  };
  return shouldAvoidSnakeCasing ? defaultQuery : Object.assign({}, defaultQuery, {
    casing: 'snake'
  });
};
export const toFieldChanges = data => {
  if (!data) return convertJsonToFieldValueAvro(null);
  if (data.content) {
    const {
        content,
        gridLayouts
      } = data,
      rest = _objectWithoutPropertiesLoose(data, _excluded);
    const fields = Object.assign({}, content);
    if (gridLayouts) {
      fields.grid_layouts = gridLayouts;
    }

    // `content` is the authoritative edited field set, so it wins on key
    // collisions: top-level `rest` keys only fill in fields not already present
    // in `content`. (Today this branch only runs for grid-layout saves, where
    // `rest` is empty, so no collision occurs in practice.)
    Object.keys(rest).forEach(key => {
      if (!(key in fields)) {
        fields[key] = rest[key];
      }
    });
    return convertJsonToFieldValueAvro(convertObjectToCamelCase(fields));
  }
  return convertJsonToFieldValueAvro(convertObjectToCamelCase(data));
};
export const autoSaveStagedPage = (contentId, data) => {
  const fieldChanges = toFieldChanges(data);
  return rpcClient.call(autoSaveStagedPageRpc, {
    request: {
      contentId,
      fieldChanges
    }
  });
};
export const manualSaveStagedPage = (contentId, data) => {
  const fieldChanges = toFieldChanges(data);
  return rpcClient.call(manualSaveStagedPageRpc, {
    request: {
      contentId,
      fieldChanges
    }
  });
};
export const save = ({
  baseContentUrl,
  buffer,
  data,
  query,
  isEditingGridLayout
}) => {
  // TODO: only use /grid-buffer for buffer saving once revision hisotry is ready for grid layout
  if (isEditingGridLayout && buffer) {
    baseContentUrl = `${baseContentUrl}/grid-buffer`;
  } else if (buffer) {
    baseContentUrl = `${baseContentUrl}/buffer`;
  }

  // TEMP: Certain content properties on a page require a "hard save" directly to the
  // non-buffer cospages endpoint. That endpoint expexts the save data in snake casing
  // while grids uses camel casing, so we need to convert the data before calling
  // the endpoint. This should be removed once a BE solution is built.
  // Context: https://hubspot.slack.com/archives/C09LHT5SWRH/p1762466700117529
  if (isEditingGridLayout && !buffer) {
    const saveData = convertObjectToSnakeCase(data.content);
    return httpWithNonIdempotentRetries.putWithResponse(baseContentUrl, {
      data: saveData,
      query,
      timeout: SAVE_TIMEOUT
    });
  }
  return httpWithNonIdempotentRetries.putWithResponse(baseContentUrl, {
    data,
    query,
    timeout: SAVE_TIMEOUT
  });
};

// TODO make a more flexible approach for structured content save methods
// as not every consumer should be required to make a patch for updates
export const structuredContentSaveRequest = ({
  baseContentUrl,
  data,
  query
}) => {
  if (!baseContentUrl) {
    throw new Error('baseContentUrl is missing. Make sure makeSaveActions has the apiUrl property.');
  }
  return httpWithNonIdempotentRetries.patchWithResponse(baseContentUrl, {
    data,
    query,
    timeout: SAVE_TIMEOUT
  });
};